import UtilityService from "./UtilityService";
import { postToAgents } from "./PlaybookApi";

const ALWAYS_AVAILABLE_TOOL_GROUP = {
	key: "",
	label: "Always available",
	reads: "Needs no linked source: the client's own record, skills and reference files, last month's decisions, and the files a run produces.",
};
const TOOL_EFFECTS_IN_USE = ["reads_only", "proposes", "produces_file"];

const ToolLibraryService = {
	/**
	 * Every tool an agent or a skill can use, with the source it reads.
	 *
	 * @return {Promise<Array>} [{ name, label, needs, description }]
	 */
	listToolCatalogue() {
		return postToAgents({ operationId: "listPlaybookToolCatalogue", body: {} });
	},

	/**
	 * The firm's toolbox: every tool with what it does to the world and how many skills and
	 * playbooks use it.
	 *
	 * @return {Promise<Array>}
	 */
	listToolbox() {
		return postToAgents({ operationId: "listPlaybookToolbox", body: {} });
	},

	/**
	 * How a tool's effect on the world reads on screen: the label, the dot tone beside it and
	 * the sentence behind the tooltip.
	 *
	 * @param {Object} params
	 * @param {String} params.effect reads_only, proposes or produces_file
	 * @return {Object} { label, tone, detail }
	 */
	describeToolEffect({ effect }) {
		const effects = {
			reads_only: { label: "Read only", tone: "neutral", detail: "Reads records from the source; never changes anything." },
			proposes: {
				label: "Propose only",
				tone: "warning",
				detail: "Prepares a change (a journal entry, a task) for a person to review and execute; nothing happens until they do.",
			},
			produces_file: {
				label: "Create file",
				tone: "success",
				detail: "Produces a file (Excel, PowerPoint, PDF) on the run for download; changes nothing else.",
			},
			execute: { label: "Execute", tone: "danger", detail: "Would make a change directly. Not enabled for any tool: the registry refuses it." },
		};
		const described = effects[effect] || { label: "Unknown", tone: "danger", detail: "This tool has not said what it does." };
		return described;
	},

	/**
	 * How many skills and playbooks use a tool, in words.
	 *
	 * @param {Object} params
	 * @param {Number} params.usedBySkillCount
	 * @param {Number} params.usedByPlaybookCount
	 * @return {String}
	 */
	describeToolUsage({ usedBySkillCount, usedByPlaybookCount }) {
		const skillCount = Number(usedBySkillCount) || 0;
		const playbookCount = Number(usedByPlaybookCount) || 0;
		if (skillCount === 0 && playbookCount === 0) return "Not used yet";
		const parts = [];
		if (skillCount > 0) parts.push(UtilityService.describeCount({ count: skillCount, singular: "skill" }));
		if (playbookCount > 0) parts.push(UtilityService.describeCount({ count: playbookCount, singular: "playbook" }));
		const usage = `Used by ${parts.join(" · ")}`;
		return usage;
	},

	/**
	 * The first sentence of a tool's description — the model-facing text is long, and the first
	 * sentence says what the tool gives.
	 *
	 * @param {Object} params
	 * @param {String} params.description
	 * @return {String}
	 */
	toolSummary({ description }) {
		const text = String(description || "").trim();
		const firstSentenceEnd = text.search(/\.\s/);
		const summary = firstSentenceEnd === -1 ? text : text.slice(0, firstSentenceEnd + 1);
		return summary;
	},

	/**
	 * The tools in a catalogue grouped by the source each reads, tools that need no source first.
	 * A group is labelled with its source's name and says what the source reads, as the catalogue
	 * gives them for every tool that needs a source (sourceLabel, sourceReads).
	 *
	 * @param {Object} params
	 * @param {Array} params.tools [{ name, label, needs, sourceLabel, sourceReads }]
	 * @return {Array} [{ key, label, reads, tools }]
	 */
	groupToolsBySource({ tools }) {
		const groups = [];
		(tools || []).forEach(tool => {
			const key = tool.needs || ALWAYS_AVAILABLE_TOOL_GROUP.key;
			let group = groups.find(candidate => candidate.key === key);
			if (!group) {
				const label = key ? tool.sourceLabel : ALWAYS_AVAILABLE_TOOL_GROUP.label;
				const reads = key ? tool.sourceReads : ALWAYS_AVAILABLE_TOOL_GROUP.reads;
				group = { key, label, reads, tools: [] };
				groups.push(group);
			}
			group.tools.push(tool);
		});
		const alwaysAvailable = groups.filter(group => group.key === ALWAYS_AVAILABLE_TOOL_GROUP.key);
		const bySource = groups.filter(group => group.key !== ALWAYS_AVAILABLE_TOOL_GROUP.key);
		const ordered = [...alwaysAvailable, ...bySource];
		return ordered;
	},

	/**
	 * The toolbox groups to show: within each source group, only the tools whose label,
	 * description or category match the search and the chosen category, access and usage.
	 * Empty groups drop out.
	 *
	 * @param {Object} params
	 * @param {Array} params.groups from groupToolsBySource
	 * @param {String} params.search
	 * @param {String} [params.category] "" for any
	 * @param {String} [params.effect] "" for any
	 * @param {Boolean} [params.isOnlyUsed] only tools some skill or playbook uses
	 * @return {Array}
	 */
	toolGroupsMatching({ groups, search, category = "", effect = "", isOnlyUsed = false }) {
		const searchText = UtilityService.toSearchText(search);
		const narrowed = groups.map(group => {
			const tools = group.tools.filter(tool => {
				if (category && tool.category !== category) return false;
				if (effect && tool.effect !== effect) return false;
				if (isOnlyUsed && !(tool.usedBySkillCount > 0 || tool.usedByPlaybookCount > 0)) return false;
				if (!searchText) return true;
				const haystack = UtilityService.toSearchText(`${tool.label} ${tool.description || ""} ${tool.category || ""}`);
				const isMatch = haystack.includes(searchText);
				return isMatch;
			});
			return { ...group, tools };
		});
		const withTools = narrowed.filter(group => group.tools.length > 0);
		return withTools;
	},

	/**
	 * The categories the toolbox's tools fall under, A to Z, as picker choices.
	 *
	 * @param {Object} params
	 * @param {Array} params.tools
	 * @return {Array} [{ value, label }]
	 */
	toolCategoryChoices({ tools }) {
		const names = [...new Set((tools || []).map(tool => tool.category).filter(Boolean))].sort();
		const choices = names.map(name => ({ value: name, label: name }));
		return choices;
	},

	/**
	 * What a tool can do to the world, in the order a reader should learn it: the three effects
	 * the registry allows, then the one it refuses (execute), so the limit is said out loud.
	 *
	 * @return {Array} [{ effect, label, tone, detail, isInUse }]
	 */
	toolEffectLegend() {
		const legend = [...TOOL_EFFECTS_IN_USE, "execute"].map(effect => ({
			effect,
			...this.describeToolEffect({ effect }),
			isInUse: TOOL_EFFECTS_IN_USE.includes(effect),
		}));
		return legend;
	},
};

export default ToolLibraryService;
